import { Editor, Element, Node, Path } from 'slate';

import { resolveParagraphEntryByLocationAnchor } from './resolve-location-anchor';
import { stampReasoningOnFirstSourcedEdit } from './suggestion-reasoning';
import { htmlToAline } from '../../../serializers/html';
import type { AlineDescendant } from '../../internal';
import { AlineTransforms, EditingMode, isParagraphElement, Queries } from '../../internal';
import type { SuggestionSource } from '../../types';

export interface ApplyInsertOutputOptions {
  /**
   * The author ID for the suggested edits (typically the AI or system user)
   */
  authorId: string;
  /**
   * Identifies the whole insertion as one group, so accept/reject acts on it
   * as a unit
   */
  groupId: string;
  /**
   * The HTML content to insert (already stripped of any echoed markers)
   */
  html: string;
  /**
   * The locationId of the existing paragraph the insertion is relative to
   */
  locationAnchor: string;
  /**
   * Whether the content goes immediately before or after the anchor paragraph
   */
  position: 'before' | 'after';
  /**
   * Why the content is being inserted — stored on the first sourced edit of
   * the inserted content
   */
  reasoning: string;
  /**
   * The source stamp carried by every inserted text edit — or undefined to
   * materialize the insertion as the author's own tracked changes, with no
   * source stamps and no stored reasoning (no AI chrome).
   */
  source: SuggestionSource | undefined;
}

/**
 * Applies an `insert` structured edit output to the editor as a suggested
 * insertion.
 *
 * The flow:
 * 1. Finds the anchor paragraph by locationId.
 * 2. Converts the HTML to Aline blocks via `htmlToAline` and registers any
 *    list definitions it produced.
 * 3. Inserts the blocks before/after the anchor in SUGGESTING mode, which
 *    marks every inserted text node with an `insertNode` suggested edit.
 * 4. Stamps the caller's source onto those edits so they're attributed like
 *    other AI redlines.
 * 5. Stores the reasoning on the first sourced edit of the inserted content.
 *    Block suggestions carry no suggestion-marker pair — the sourced edits
 *    themselves are the suggestion, and readers resolve the group by its
 *    groupId.
 */
export function applyInsertOutput(editor: Editor, options: ApplyInsertOutputOptions): void {
  const { authorId, groupId, html, locationAnchor, position, reasoning, source } = options;

  const [anchorNode, anchorPath] = resolveParagraphEntryByLocationAnchor(editor, locationAnchor);

  /**
   * Pass the anchor as the insertion location so inserted paragraphs borrow
   * spacing from the nearby content. This path has no selection, so
   * `htmlToAline` couldn't infer the location on its own.
   */
  const { blocks, listDefinitions } = htmlToAline(editor, { at: anchorPath, html });
  if (blocks.length === 0) {
    throw new Error(`Insert html for anchor '${locationAnchor}' produced no content`);
  }

  /**
   * List continuation: when inserting next to a list item, adopt that list so
   * the inserted items keep its numbering. Rewrite each inserted top-level list
   * paragraph onto the anchor's `listId` (reusing the anchor's existing
   * definition); the definitions `htmlToAline` minted for them are then
   * throwaway unless a non-rewritten paragraph still references them (see the
   * registration loop below). Inserts next to a non-list paragraph keep their
   * own new list. (Only top-level blocks are touched, so list paragraphs
   * inside an inserted table keep their own list — same boundary as spacing.)
   */
  const anchorListId = anchorNode.listProps?.listId;
  const adoptedListIds = new Set<string>();
  if (anchorListId) {
    for (const block of blocks) {
      if (isParagraphElement(block) && block.listProps) {
        adoptedListIds.add(block.listProps.listId);
        block.listProps = { ...block.listProps, listId: anchorListId };
      }
    }
  }

  /**
   * Adoption only rewrites top-level paragraphs, so a minted list id can still
   * be referenced deeper in the inserted blocks (a list paragraph inside an
   * inserted table cell shares the top-level items' minted id). Registration
   * is skipped only for minted definitions nothing references anymore.
   */
  const referencedListIds = getReferencedListIds(blocks);
  for (const [listDefinitionId, listDefinition] of Object.entries(listDefinitions)) {
    if (adoptedListIds.has(listDefinitionId) && !referencedListIds.has(listDefinitionId)) continue;
    AlineTransforms.createListDefinition(editor, { listDefinition, listDefinitionId });
  }

  const insertPath = position === 'before' ? anchorPath : Path.next(anchorPath);
  const parentPath = insertPath.slice(0, -1);
  const firstIndex = Queries.indexInParent(insertPath);

  /**
   * `insertContentNodes` runs the blocks through `cleanContentNodes`, which can
   * drop or split a top-level node, so the number of nodes that land may differ
   * from `blocks.length`. Measure the parent's child count before and after to
   * get the real inserted range — deriving paths from `blocks.length` would
   * mis-target the reasoning stamp when the counts diverge.
   */
  const childCountBefore = Node.parent(editor, insertPath).children.length;

  /**
   * SUGGESTING mode marks every inserted text node with an `insertNode`
   * suggested edit (same as pasting with track changes on), so the
   * insertion is rejectable. `merge: false` keeps the inserted blocks as
   * separate siblings instead of merging into the anchor paragraph.
   */
  AlineTransforms.insertContentNodes(editor, {
    at: insertPath,
    authorId,
    editingMode: EditingMode.SUGGESTING,
    merge: false,
    nodes: blocks,
    select: 'none',
    source,
  });

  const insertedCount = Node.parent(editor, insertPath).children.length - childCountBefore;
  if (insertedCount <= 0) {
    throw new Error(`Insert for anchor '${locationAnchor}' added no content after cleaning`);
  }
  const firstBlockPath = [...parentPath, firstIndex];
  const lastBlockPath = [...parentPath, firstIndex + insertedCount - 1];

  // A sourceless insertion has no sourced edit to stamp — and no AI reasoning
  // belongs on the author's own tracked changes.
  if (!source) return;

  stampReasoningOnFirstSourcedEdit(editor, {
    at: Editor.range(editor, firstBlockPath, lastBlockPath),
    groupId,
    reasoning,
  });
}

/**
 * Every listId referenced by a paragraph anywhere in the nodes — top-level
 * list paragraphs and list paragraphs nested inside tables alike.
 */
function getReferencedListIds(nodes: AlineDescendant[]): Set<string> {
  const listIds = new Set<string>();
  for (const node of nodes) {
    if (!Element.isElement(node)) continue;
    if (isParagraphElement(node) && node.listProps) listIds.add(node.listProps.listId);
    for (const listId of getReferencedListIds(node.children)) listIds.add(listId);
  }
  return listIds;
}
